import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Stack, breakpoints, Inline, LinkButton, Input, Button } from '@etg/wings';
import { useDateFormat, useRevolut, useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { CaretDown } from '@phosphor-icons/react';
import { m as motion } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { useMediaQuery } from 'usehooks-ts';
import RebookingBanner from '../../../common/components/RebookingBanner';
import { cabinClasses } from '../../../constants/cabinClassesConstants';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { Notification, variants } from '@eti/components';
import {
  CABIN_CLASS,
  DEPARTURE_DATE,
  DESTINATION,
  ORIGIN,
  SINGLE_BOUND,
  TRIP_TYPE,
} from '../constants/formFields';
import type { SearchFormValues } from '../../../common/search-form-panel/SearchFormContainer';
import type { RebookingData } from '../../../utils/rebooking';
import type { DateFormat } from '../types/dateFormat';
import {
  extendedSearchFormStyles,
  iconStyles,
  linkButtonStyles,
  motionVariantsExtendedOptions,
  datesAndPassengersTransition,
} from './styles/searchFormStyles';
import Dates from './Dates';
import SubmitButton from './SubmitButton';
import Bound from './Bound';
import TripTypes from './TripTypes';
import Bounds from './Bounds';
import DirectFlightCheckBox from './DirectFlightCheckBox';
import ExtendedSearchFormFields from './ExtendedSearchFormFields';
import ZeroWidthSpace from './ZeroWidthSpace';
import Passengers from './passengers/Passengers';
import CabinClassSelect from './CabinClassSelect';
import OriginAndDestination from './OriginAndDestination';
import { useLocation } from 'react-router-dom';

const { BUSINESS, ECONOMY, FIRST, PREMIUM_ECONOMY } = cabinClasses;

const formWrapperStyles = (isMultiStop: boolean) => css`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: ${isMultiStop ? 'column' : 'row'};
  }
`;

const relativePositionStyles = css`
  position: relative;
`;

const wideFlexStyles = css`
  flex: 3;
`;

interface AlternateSingleBoundLayoutProps {
  cabinClass: ReactNode;
  dates: ReactNode;
  originAndDestination: ReactNode;
  passengers: ReactNode;
  submitButton: ReactNode;
  tripType: ReactNode;
}

const AlternateSingleBoundLayout = ({
  cabinClass,
  dates,
  originAndDestination,
  passengers,
  submitButton,
  tripType,
}: AlternateSingleBoundLayoutProps) => {
  return (
    <Stack spacing={16}>
      <Stack spacing={8}>
        <Inline spacing={8}>
          {tripType}
          {cabinClass}
        </Inline>
        <Inline spacing={8}>{originAndDestination}</Inline>
        <Inline className={relativePositionStyles} spacing={8}>
          {dates}
          {passengers}
        </Inline>
      </Stack>
      {submitButton}
    </Stack>
  );
};

const flexNoneStyles = css`
  flex: none;
`;

const flexOneStyles = css`
  flex: 1;
`;

const buttonStyles = css`
  width: 100%;
`;

const alternateTripTypeLayoutStyles = css`
  min-width: 100px;
`;

const inputGroupStyles = css`
  flex: 1;
  position: relative;
  width: calc(50% - 24px);
`;

const PassengersAndCabinClassWrapper = ({ children }: { children: ReactNode }) => {
  const isDesktopView = useMediaQuery(`(min-width: ${breakpoints._768})`);

  return isDesktopView ? (
    <Input.Group className={inputGroupStyles}>{children}</Input.Group>
  ) : (
    <Stack spacing={24}>{children}</Stack>
  );
};

const passengersLayoutStyles = css`
  flex: 1;
`;

type SearchFormProps = {
  rebookingData?: RebookingData;
  shouldShowRebookingBanner?: boolean;
};

const SearchForm = ({ rebookingData, shouldShowRebookingBanner }: SearchFormProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isRevolut } = useRevolut();
  const { date } = useDateFormat() as { date: DateFormat };
  const { watch } = useFormContext<SearchFormValues>();
  const location = useLocation();
  const hasDiscountExpiredError =
    new URLSearchParams(location.search).get('discount-bind-error') === 'discount-expired';
  const [
    selectedTripType,
    origin,
    destination,
    returnTimeOfDay,
    departureTimeOfDay,
    selectedCarriers,
  ] = watch([
    'tripType',
    'singleBound.origin',
    'singleBound.destination',
    'singleBound.returnTimeOfDay',
    'singleBound.departureTimeOfDay',
    'carrierCodes',
  ]);
  const [areOriginAndDestinationFilled, setAreOriginAndDestinationFilled] = useState(
    Boolean(origin && destination),
  );
  const { isOpen: isMoreOptionsOpen, handleToggle } = useToggle(
    Boolean(returnTimeOfDay || departureTimeOfDay || selectedCarriers),
  );
  const formConstraints = rebookingData?.formConstraints || null;
  const isMultiStop = selectedTripType === tripTypes.MULTI_STOP;
  const submitButtonRef = useRef(null);
  const departureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (origin && destination) {
      setAreOriginAndDestinationFilled(true);
    }
  }, [origin, destination]);

  const getFieldName = (fieldName: string) =>
    selectedTripType !== tripTypes.MULTI_STOP ? `${SINGLE_BOUND}.${fieldName}` : '';
  const isExtendedSearchFormEnabled = p('SearchForm.ShowExtendedForm.Enabled');
  const shouldShowDirectFlightCheckbox = p('SearchForm.DirectFlights.Checkbox.Enabled');

  const onDatesAnimationStart = () => {
    const isSearchButtonFocused = document.activeElement === submitButtonRef.current;

    if (isSearchButtonFocused) {
      departureInputRef.current?.focus();
    }
  };

  const cabinClassLayoutStyles = isRevolut ? flexNoneStyles : flexOneStyles;
  const cabinClassOptions = [
    { value: ECONOMY, label: t('Air.Search.Cabin.Button.Economy') },
    { value: PREMIUM_ECONOMY, label: t('Air.Search.Cabin.Button.PremiumEconomy') },
    { value: BUSINESS, label: t('Air.Search.Cabin.Button.Business') },
    { value: FIRST, label: t('Air.Search.Cabin.Button.Supersonic') },
  ];

  return isRevolut ? (
    <AlternateSingleBoundLayout
      cabinClass={
        <CabinClassSelect
          cabinClasses={cabinClassOptions}
          className={cabinClassLayoutStyles}
          data-testid="searchForm-cabinClasses-dropdown"
          fieldName={CABIN_CLASS}
          label={t('Searchform.BookingClass.Label')}
          placeholder={t('Air.Search.Cabin.Placeholder')}
          shouldMarkInputAsRequired={false}
        />
      }
      dates={
        <Dates
          boundIndex={0}
          dateFormat={date}
          departureDateName={getFieldName(DEPARTURE_DATE)}
          departureInputRef={departureInputRef}
          selectedTripType={selectedTripType}
        />
      }
      originAndDestination={
        <OriginAndDestination
          boundIndex={0}
          destinationName={`${SINGLE_BOUND}.${DESTINATION}`}
          originName={`${SINGLE_BOUND}.${ORIGIN}`}
          setAreOriginAndDestinationFilled={setAreOriginAndDestinationFilled}
        />
      }
      passengers={<Passengers constraints={formConstraints?.passengers} />}
      submitButton={
        <Button
          className={buttonStyles}
          data-testid="searchForm-searchFlights-button"
          ref={submitButtonRef}
          type="submit"
          variant="primary"
        >
          {t('Button.Text.Search.Air')}
        </Button>
      }
      tripType={<TripTypes className={alternateTripTypeLayoutStyles} fieldName={TRIP_TYPE} />}
    />
  ) : (
    <>
      <Stack spacing={24}>
        {rebookingData && shouldShowRebookingBanner && <RebookingBanner />}
        {hasDiscountExpiredError && (
          <Notification
            dataTestId="discountExpiredNotification"
            title={t('SelfServiceRebooking.Error.Title')}
            variant={variants.WARNING}
          >
            {t('SelfServiceRebooking.Error.Text')}
          </Notification>
        )}
        <TripTypes fieldName={TRIP_TYPE} />
        <Stack className={formWrapperStyles(isMultiStop)} spacing={24}>
          <Stack
            className={wideFlexStyles}
            spacing={areOriginAndDestinationFilled || isMultiStop ? 24 : 0}
          >
            {!isMultiStop ? (
              <>
                <Bound
                  boundIndex={0}
                  dateFormat={date}
                  selectedTripType={selectedTripType}
                  setAreOriginAndDestinationFilled={setAreOriginAndDestinationFilled}
                />
                {(areOriginAndDestinationFilled || isMultiStop) && (
                  <motion.div
                    animate={datesAndPassengersTransition.animate}
                    data-testid="date-time-animation-wrapper"
                    initial={datesAndPassengersTransition.initial}
                    onAnimationStart={onDatesAnimationStart}
                    transition={datesAndPassengersTransition.transition}
                  >
                    <Inline
                      collapseBelow={breakpoints._768}
                      spacing={{
                        [breakpoints._0]: 24,
                        [breakpoints._768]: 48,
                      }}
                    >
                      <Dates
                        boundIndex={0}
                        dateFormat={date}
                        departureDateName={getFieldName(DEPARTURE_DATE)}
                        departureInputRef={departureInputRef}
                        selectedTripType={selectedTripType}
                      />
                      <PassengersAndCabinClassWrapper>
                        <Passengers
                          className={passengersLayoutStyles}
                          constraints={formConstraints?.passengers}
                        />
                        <CabinClassSelect
                          cabinClasses={cabinClassOptions}
                          className={cabinClassLayoutStyles}
                          data-testid="searchForm-cabinClasses-dropdown"
                          fieldName={CABIN_CLASS}
                          label={t('Searchform.BookingClass.Label')}
                          placeholder={t('Air.Search.Cabin.Placeholder')}
                          shouldMarkInputAsRequired={false}
                        />
                      </PassengersAndCabinClassWrapper>
                    </Inline>
                    {isExtendedSearchFormEnabled && (
                      <div className={extendedSearchFormStyles}>
                        <Inline spacing={8}>
                          <LinkButton
                            className={linkButtonStyles}
                            data-testid="more-search-options-button"
                            onClick={handleToggle}
                          >
                            {t('Searchform.MoreSearchOptions.Link.Text')}
                            <CaretDown
                              className={iconStyles(isMoreOptionsOpen)}
                              size={20}
                              weight="light"
                            />
                          </LinkButton>
                        </Inline>
                        <motion.div
                          animate={isMoreOptionsOpen ? 'show' : 'hide'}
                          data-testid="more-options-animation-wrapper"
                          initial={false}
                          variants={motionVariantsExtendedOptions}
                        >
                          <Stack spacing={24}>
                            <ExtendedSearchFormFields selectedTripType={selectedTripType} />
                            {shouldShowDirectFlightCheckbox && <DirectFlightCheckBox />}
                          </Stack>
                        </motion.div>
                      </div>
                    )}
                  </motion.div>
                )}
              </>
            ) : (
              <>
                <Bounds dateFormat={date} selectedTripType={selectedTripType} />
                <Inline
                  collapseBelow={breakpoints._768}
                  spacing={{
                    [breakpoints._0]: 24,
                    [breakpoints._768]: 48,
                  }}
                >
                  <Stack spacing={4}>
                    <ZeroWidthSpace isDesktopOnly />
                    {shouldShowDirectFlightCheckbox && <DirectFlightCheckBox />}
                  </Stack>
                  <PassengersAndCabinClassWrapper>
                    <Passengers
                      className={passengersLayoutStyles}
                      constraints={formConstraints?.passengers}
                    />
                    <CabinClassSelect
                      cabinClasses={cabinClassOptions}
                      className={cabinClassLayoutStyles}
                      data-testid="searchForm-cabinClasses-dropdown"
                      fieldName={CABIN_CLASS}
                      label={t('Searchform.BookingClass.Label')}
                      placeholder={t('Air.Search.Cabin.Placeholder')}
                      shouldMarkInputAsRequired={false}
                    />
                  </PassengersAndCabinClassWrapper>
                  <SubmitButton isMultiStop ref={submitButtonRef} />
                </Inline>
              </>
            )}
          </Stack>
          {!isMultiStop && (
            <SubmitButton
              areOriginAndDestinationFilled={areOriginAndDestinationFilled}
              ref={submitButtonRef}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default SearchForm;
